import React from "react";
import "./SidebarNavigation.css";
import {
  TeamsIcon,
  GoalsIcon,
  CustomizeIcon,
  Kxd4DPlanningIcon,
  Kxd5DCostIcon,
  KxdAnalyticsIcon,
  KxdCoreIcon,
  KxdDataExplorationIcon,
  KxdVisualizationIcon,
  KxdHubIcon,
  KxdLayoutStudioIcon,
  KxdEnergyModelIcon,
  Kxd3DEditorIcon,
} from "../icons";
import NavigationItem from "./NavigationItem";
import { type NavItem } from "./types";
import { EXTERNAL_APPS } from "../../constants";

interface SidebarNavigationProps {
  activeItem?: string;
  onItemClick?: (item: string) => void;
  className?: string;
  children: React.ReactNode; // Make children required since NavigationItems is app-specific
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  activeItem = "P&ID explorer",
  onItemClick,
  className = "",
  children,
}) => {
  // Get current origin for external app URLs (preserves protocol, host, and port)
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const externalNavigationItems: NavItem[] = [
    {
      icon: <KxdHubIcon />,
      label: EXTERNAL_APPS.KONNECT_HOME.label,
      alt: EXTERNAL_APPS.KONNECT_HOME.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.KONNECT_HOME.url}/`,
    },
    {
      icon: <KxdCoreIcon />,
      label: EXTERNAL_APPS.KONNECT_CORE.label,
      alt: EXTERNAL_APPS.KONNECT_CORE.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.KONNECT_CORE.url}/`,
    },
    {
      icon: <KxdDataExplorationIcon />,
      label: EXTERNAL_APPS.DATA_EXPLORATION.label,
      alt: EXTERNAL_APPS.DATA_EXPLORATION.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.DATA_EXPLORATION.url}/`,
    },
    {
      icon: <KxdAnalyticsIcon />,
      label: EXTERNAL_APPS.ANALYTICS.label,
      alt: EXTERNAL_APPS.ANALYTICS.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.ANALYTICS.url}/`,
    },
    {
      icon: <KxdVisualizationIcon />,
      label: EXTERNAL_APPS.VISUALIZATION_3D.label,
      alt: EXTERNAL_APPS.VISUALIZATION_3D.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.VISUALIZATION_3D.url}/`,
    },
    {
      icon: <Kxd4DPlanningIcon />,
      label: EXTERNAL_APPS.PLANNING_4D.label,
      alt: EXTERNAL_APPS.PLANNING_4D.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.PLANNING_4D.url}/`,
    },
    {
      icon: <Kxd5DCostIcon />,
      label: EXTERNAL_APPS.COST_5D.label,
      alt: EXTERNAL_APPS.COST_5D.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.COST_5D.url}/`,
    },
    {
      icon: <KxdLayoutStudioIcon />,
      label: EXTERNAL_APPS.LAYOUT_STUDIO.label,
      alt: EXTERNAL_APPS.LAYOUT_STUDIO.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.LAYOUT_STUDIO.url}/`,
    },
    {
      icon: <KxdEnergyModelIcon />,
      label: EXTERNAL_APPS.ENERGY_MODEL.label,
      alt: EXTERNAL_APPS.ENERGY_MODEL.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.ENERGY_MODEL.url}/`,
    },
    {
      icon: <Kxd3DEditorIcon />,
      label: EXTERNAL_APPS.EDITOR_3D.label,
      alt: EXTERNAL_APPS.EDITOR_3D.alt,
      isExternal: true,
      path: `${origin}${EXTERNAL_APPS.EDITOR_3D.url}/`,
    },
  ];

  const bottomNavigationItems: NavItem[] = [
    {
      icon: <TeamsIcon />,
      label: "Teams",
      alt: "Teams",
      isBottom: true,
    },
    {
      icon: <GoalsIcon />,
      label: "Goals",
      alt: "Goals",
      isBottom: true,
    },
  ];

  const renderNavItem = (item: NavItem) => {
    return (
      <NavigationItem
        key={item.label}
        item={item}
        activeItem={activeItem}
        onItemClick={onItemClick}
      />
    );
  };

  const renderExternalNavItem = (item: NavItem) => {
    return (
      <NavigationItem
        key={item.label}
        item={item}
        activeItem={activeItem}
      />
    );
  };

  return (
    <aside className={`sidebar ${className}`}>
      <nav className="sidebar-nav">
        {/* Main navigation items */}
        {children}

        {/* Divider before external navigation */}
        <div className="nav-divider" />

        {/* External navigation items - Open in new tab */}
        {externalNavigationItems.map(renderExternalNavItem)}

        {/* Bottom navigation items */}
        {bottomNavigationItems.map(renderNavItem)}

        {/* Customize sidebar */}
        <NavigationItem
          item={{
            icon: <CustomizeIcon />,
            label: "Customize sidebar",
            alt: "Customize sidebar",
          }}
          activeItem={activeItem}
          onItemClick={onItemClick}
        />
      </nav>
    </aside>
  );
};

export default SidebarNavigation;
